import React, {useMemo} from "react";
import ModalV2 from "@/components/portal/ModalV2";
import {IncidentTypeDisplay} from "@/components/data-display/IncidentTypeDisplay";
import {CountryName} from "@/components/data-display/CountryName";
import {FeedName} from "@/components/data-display/FeedName";
import {IncidentSeverityDisplay} from "@/components/incident-severity/IncidentSeverityDisplay";
import {AdvancedMarker, APIProvider, Map} from "@vis.gl/react-google-maps";

type Incident = {
    id: string;
    categoryName: string;
    typeName: string;
    severity: string;
    summary: string;
    description: string;
    countryISO: string;
    town?: string;
    feedID: string;
    tags: string[];
    startedAt: string;
    endedAt?: string;
    ongoing: boolean;
    reportedAt?: string;
    updatedAt?: string;
    source?: string;
    casualties?: {
        dead?: number;
        injured?: number;
        missing?: number;
    };
    position?: {
        type: string;
        coordinates: [number, number];
    };
    reference?: Array<{
        medium: string;
        link: string;
        title: string;
        publishedAt: string;
    }>;
};

type IncidentDetailsModalProps = {
    incident: Incident | null;
    open: boolean;
    setOpen: (open: boolean) => void;
    onTagClick?: (tag: string) => void;
};

export const IncidentDetailsModal: React.FC<IncidentDetailsModalProps> = ({
    incident,
    open,
    setOpen,
    onTagClick,
}) => {
    if (!incident) return null;

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";

        // Use incident's timezone if available
        const timezone = (incident as any).timezone;
        const date = new Date(dateString);

        if (timezone) {
            // Format with the incident's timezone
            const formatter = new Intl.DateTimeFormat("en-GB", {
                timeZone: timezone,
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                timeZoneName: "short"
            });
            return formatter.format(date);
        }

        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"});
    };

    const formatCoordinates = (position?: { coordinates: [number, number] }) => {
        if (!position?.coordinates) return "N/A";
        const [lng, lat] = position.coordinates;
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    };

    const position = useMemo(() => {
        if(incident.position){
            return {
                lat: incident.position.coordinates[1],
                lng: incident.position.coordinates[0],
            }
        }
        return null
    }, [incident]);

    return (
        <ModalV2 open={open} setOpen={setOpen}>
            <div className="flex flex-col gap-6 max-h-[80vh] overflow-y-auto" data-id={incident.id}>
                {/* Header */}
                <div>
                    <h2 className="text-2xl font-bold">{incident.summary || "Incident Details"}</h2>
                </div>

                {/* Classification */}
                {(incident.categoryName || incident.typeName || (incident.tags && incident.tags.length > 0) || incident.severity || incident.feedID) && (
                    <div className="border-t border-light-light-grey pt-4">
                        <h3 className="text-lg font-bold mb-3">Classification</h3>
                        <div className="grid grid-cols-4 gap-4">
                            {(incident.categoryName || incident.typeName || (incident.tags && incident.tags.length > 0)) && (
                                <div>
                                    <label className="text-sm font-medium">Type/Tags</label>
                                    <div className="mt-1">
                                        <IncidentTypeDisplay
                                            categoryName={incident.categoryName}
                                            typeName={incident.typeName}
                                            tags={incident.tags}
                                            onTagClick={onTagClick ? (tag) => { setOpen(false); onTagClick(tag); } : undefined}
                                        />
                                    </div>
                                </div>
                            )}
                            {incident.severity && (
                                <div>
                                    <label className="text-sm font-medium">Severity</label>
                                    <div className="mt-1">
                                        <IncidentSeverityDisplay severity={incident.severity}/>
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="text-sm font-medium">Feed</label>
                                <div className="mt-1">
                                    <FeedName feedID={incident.feedID} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Timeline */}
                <div className="border-t border-light-light-grey pt-4">
                    <h3 className="text-lg font-bold mb-3">Timeline</h3>
                    <div className="grid grid-cols-4 gap-4">

                        {incident.startedAt && (
                            <div>
                                <label className="text-sm font-medium">Started</label>
                                <div className="mt-1">{formatDate(incident.startedAt)}</div>
                            </div>
                        )}
                        {incident.endedAt && (
                            <div>
                                <label className="text-sm font-medium">Ended</label>
                                <div className="mt-1">{formatDate(incident.endedAt)}</div>
                            </div>
                        )}
                        {incident.reportedAt && (
                            <div>
                                <label className="text-sm font-medium">Reported</label>
                                <div className="mt-1">{formatDate(incident.reportedAt)}</div>
                            </div>
                        )}
                        {incident.updatedAt && (
                            <div>
                                <label className="text-sm font-medium">Last Updated</label>
                                <div className="mt-1">{formatDate(incident.updatedAt)}</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Description */}
                {incident.description && (
                    <div className="border-t border-light-light-grey pt-4">
                        <h3 className="text-lg font-bold mb-3">Description</h3>
                        <p className="text-sm whitespace-pre-wrap">{incident.description}</p>
                    </div>
                )}

                {/* Location */}
                <div className="border-t border-light-light-grey pt-4">
                    <h3 className="text-lg font-bold mb-3">Location</h3>
                    <div className="grid grid-cols-4 gap-4">
                        <div>
                            <label className="text-sm font-medium">Country</label>
                            <div className="mt-1">
                                <CountryName countryISO={incident.countryISO} />
                            </div>
                        </div>
                        {incident.town && (
                            <div>
                                <label className="text-sm font-medium">Town/City</label>
                                <div className="mt-1">{incident.town}</div>
                            </div>
                        )}
                        <div>
                            <label className="text-sm font-medium">Coordinates</label>
                            <div className="mt-1 text-sm">{formatCoordinates(incident.position)}</div>
                        </div>
                    </div>
                    <div className="mt-4">
                        {position && (
                        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY!}>
                            <Map
                                mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                                defaultCenter={position}
                                defaultZoom={8}
                                gestureHandling="greedy"
                                disableDefaultUI={false}
                                zoomControl={true}
                                fullscreenControl={false}
                                streetViewControl={false}
                                mapTypeControl={true}
                                style={{width: "100%", height: "400px"}}
                            >

                                        <AdvancedMarker position={position}>
                                            <div className="w-8 h-8  rounded-full border-4 border-red shadow-lg flex items-center justify-center"
                                                style={{transform: "translate(0, 50%)"}}
                                            >
                                                <div className="w-2 h-2 bg-red rounded-full"></div>
                                            </div>
                                        </AdvancedMarker>


                            </Map>
                        </APIProvider>
                        )}
                    </div>
                </div>

                {/* Casualties */}
                {incident.casualties && (incident.casualties.dead || incident.casualties.injured || incident.casualties.missing) ? (
                    <div className="border-t border-light-light-grey pt-4">
                        <h3 className="text-lg font-bold mb-3">Casualties</h3>
                        <div className="grid grid-cols-3 gap-4">
                            {incident.casualties.dead !== undefined && incident.casualties.dead > 0 && (
                                <div>
                                    <label className="text-sm font-medium">Dead</label>
                                    <div className="mt-1 text-lg font-bold text-red-600">{incident.casualties.dead}</div>
                                </div>
                            )}
                            {incident.casualties.injured !== undefined && incident.casualties.injured > 0 && (
                                <div>
                                    <label className="text-sm font-medium">Injured</label>
                                    <div className="mt-1 text-lg font-bold text-orange-600">{incident.casualties.injured}</div>
                                </div>
                            )}
                            {incident.casualties.missing !== undefined && incident.casualties.missing > 0 && (
                                <div>
                                    <label className="text-sm font-medium">Missing</label>
                                    <div className="mt-1 text-lg font-bold text-yellow-600">{incident.casualties.missing}</div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}

                {/* References */}
                {incident.reference && incident.reference.length > 0 && (
                    <div className="border-t border-light-light-grey pt-4">
                        <h3 className="text-lg font-bold mb-3">References</h3>
                        <div className="flex flex-col gap-2">
                            {incident.reference.map((ref, index) => (
                                <div key={index} className="flex gap-4 grey pt-4">
                                    <a
                                        href={ref.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue font-bold no-underline hover:underline"
                                    >
                                        {ref.medium || ref.title || ref.link}
                                    </a>
                                        <div className={" flex-grow overflow-ellipsis"}>{ref.title}</div>

                                    <div>{formatDate(ref.publishedAt)}</div>



                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ModalV2>
    );
};
